/**
 * Fechas, en un solo lugar y con una sola regla: **nunca "Invalid Date"**.
 *
 * El bug que originó este archivo: `/postulaciones` pintaba `new Date(app.sent_at)`
 * y `sent_at` sólo existe cuando la postulación SALIÓ. Una fila en borrador o en
 * cola no tiene esa fecha, así que `new Date(undefined)` daba `Invalid Date` —
 * y como casi ninguna fila estaba enviada todavía, la columna entera se leía
 * como un error del producto. El Panel nunca lo mostró porque pinta `created_at`,
 * que el modelo estampa siempre.
 *
 * De ahí las dos mitades del arreglo:
 *
 *  1. La pantalla elige el campo correcto (`sent_at ?? created_at`).
 *  2. El formateador nunca puede escupir la palabra del motor de JavaScript. Un
 *     valor ausente y una fecha basura son lo mismo para quien lee: no sabemos
 *     la fecha. Eso se dice con una raya.
 *
 * `fallback` es un parámetro porque el guion no siempre cabe: dentro de una
 * frase ("Te invitaron el —") queda peor que no decir nada, y ahí la pantalla
 * pasa cadena vacía y esconde la línea entera.
 */

import { intlLocale } from '~/i18n/locale';

// El idioma de la app en el momento de formatear (ver `src/i18n`). Quien pinta
// estas fechas en un componente llama `useLocale()` para re-renderizar al
// cambiarlo.

type DateInput = string | number | Date | null | undefined;

/** El guion de "no sabemos", el mismo en toda la app. */
export const NO_DATE = '—';

function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** 28 ago 2026 — o el `fallback` si no hay fecha que valga. */
export function formatDate(value: DateInput, fallback: string = NO_DATE): string {
  const date = toDate(value);
  if (!date) return fallback;
  return date.toLocaleDateString(intlLocale(), { day: 'numeric', month: 'short', year: 'numeric' });
}

/** 28 de agosto de 2026 — la forma larga, para una frase. */
export function formatLongDate(value: DateInput, fallback: string = NO_DATE): string {
  const date = toDate(value);
  if (!date) return fallback;
  return date.toLocaleDateString(intlLocale(), { day: 'numeric', month: 'long', year: 'numeric' });
}

/** 28 ago 2026, 10:04 — cuando la hora importa (una tabla de operación). */
export function formatDateTime(value: DateInput, fallback: string = NO_DATE): string {
  const date = toDate(value);
  if (!date) return fallback;
  return date.toLocaleString(intlLocale(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** ¿Hay algo que mostrar? Para esconder una línea entera en vez de escribir la raya. */
export function hasDate(value: DateInput): boolean {
  return toDate(value) !== null;
}
